import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "WRITING", href: "#archive" },
  { label: "COMMENTARY", href: "#commentary" },
  { label: "ARCHIVE", href: "#archive" },
  { label: "BLOG", href: "#blog" },
  { label: "ABOUT", href: "#about" },
  { label: "SPEAKING", href: "/speaking", route: true },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => window.__lenis?.scrollTo(href, { offset: -20, duration: 1.4 }), 80);
  };

  const linkCls =
    "font-meta text-[11px] tracking-[0.2em] text-blue-100/50 hover:text-white transition-colors duration-300 px-2 py-1 whitespace-nowrap";

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 1.6, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 left-0 right-0 z-50 px-4 pt-4"
        data-testid="main-nav"
      >
        <nav className="flex items-center justify-between gap-1 backdrop-blur-md bg-[#080D1C]/70 border border-blue-100/15 rounded-full px-4 sm:px-6 py-2.5 shadow-[0_0_50px_rgba(79,124,255,0.14)] max-w-3xl mx-auto">
          <a
            href="#top"
            onClick={(e) => go(e, "#top")}
            data-testid="nav-logo"
            className="font-display font-extrabold text-sm tracking-tight whitespace-nowrap text-aura-gradient"
          >
            JW.
          </a>
          <div className="hidden sm:flex items-center gap-1">
            {LINKS.map((l) =>
              l.route ? (
                <Link key={l.label} to={l.href} data-testid={`nav-link-${l.label.toLowerCase()}`} className={linkCls}>
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className={linkCls}
                >
                  {l.label}
                </a>
              )
            )}
            <Link
              to="/book"
              data-testid="nav-link-book"
              className="ml-1 font-meta text-[11px] tracking-[0.2em] bg-[#4F7CFF] text-white rounded-full px-3.5 py-1.5 hover:bg-white hover:text-black transition-colors duration-300 whitespace-nowrap"
            >
              BOOK
            </Link>
          </div>
          <button
            onClick={() => setOpen(true)}
            data-testid="nav-menu-button"
            aria-label="Open menu"
            className="sm:hidden flex items-center gap-2 font-meta text-[10px] tracking-[0.25em] text-blue-100/70 border border-blue-100/20 rounded-full px-3.5 py-1.5"
          >
            <Menu className="w-3.5 h-3.5" /> MENU
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{ position: "fixed", zIndex: 80 }}
            className="fixed inset-0 z-[80] bg-[#05070F]/98 backdrop-blur-xl flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 pt-6">
              <span className="font-display font-extrabold text-sm tracking-tight text-aura-gradient">JW.</span>
              <button
                onClick={() => setOpen(false)}
                data-testid="nav-menu-close"
                aria-label="Close menu"
                className="flex items-center gap-2 font-meta text-[10px] tracking-[0.25em] text-blue-100/70 border border-blue-100/20 rounded-full px-3.5 py-1.5"
              >
                <X className="w-3.5 h-3.5" /> CLOSE
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-5 gap-1">
              {LINKS.map((l, i) => {
                const inner = (
                  <>
                    <span className="font-meta text-[10px] tracking-[0.25em] text-blue-300/50">
                      0{i + 1}
                    </span>
                    <span className="font-display text-[clamp(1.15rem,5.2vw,2rem)] leading-none font-extrabold uppercase tracking-tight whitespace-nowrap group-active:text-[#4F7CFF] transition-colors duration-200">
                      {l.label}
                    </span>
                  </>
                );
                const rowCls = "group flex items-baseline gap-3 border-b border-blue-100/10 py-4";
                return (
                  <motion.div
                    key={l.label}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.05, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {l.route ? (
                      <Link to={l.href} onClick={() => setOpen(false)} data-testid={`mobile-nav-link-${l.label.toLowerCase()}`} className={rowCls}>
                        {inner}
                      </Link>
                    ) : (
                      <a href={l.href} onClick={(e) => go(e, l.href)} data-testid={`mobile-nav-link-${l.label.toLowerCase()}`} className={rowCls}>
                        {inner}
                      </a>
                    )}
                  </motion.div>
                );
              })}
              <motion.div
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.08 + LINKS.length * 0.05, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                <Link
                  to="/book"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-link-book"
                  className="flex items-baseline gap-3 border-b border-blue-100/10 py-4"
                >
                  <span className="font-meta text-[10px] tracking-[0.25em] text-blue-300/50">→</span>
                  <span className="font-display text-[clamp(1.15rem,5.2vw,2rem)] leading-none font-extrabold uppercase tracking-tight text-[#4F7CFF]">
                    BOOK JEREMIAH
                  </span>
                </Link>
              </motion.div>
            </nav>
            <p className="font-meta text-[9px] tracking-[0.3em] text-blue-200/40 text-center pb-8">
              THE FUTURE IS MADE IN THE PRESENT.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
